/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { HTTP_METHOD } from "@nf/constants";
import {
  ICreateOrderRequestModel,
  IPaymentOrderResponseModel,
  IPaymentOfferListModel,
  IPaymentInvoicesListResponseModel,
  IPaymentDetailsResponseModel,
  IValidateCouponResponseModel,
  IOrderStatusResponseModel,
  ICreatePaymentDetailsRequestModel,
  IGetOffersRequestModel,
  IProductListModel,
} from "../models";
import { skymillHttpService } from "../../../../services";

type TCache = {
  offers: IPaymentOfferListModel | null;
};

export class PaymentsService {
  private cache: TCache = {
    offers: null,
  };

  get url(): string {
    return "/v1";
  }

  get urlV2(): string {
    return "/v2";
  }

  public getOffers = async (
    data?: IGetOffersRequestModel
  ): Promise<IPaymentOfferListModel> => {
    if (this.cache.offers !== null) {
      return this.cache.offers;
    }

    const offers = await skymillHttpService.request<IPaymentOfferListModel>({
      axiosConfig: {
        params: data,
      },
      method: HTTP_METHOD.GET,
      url: `${this.urlV2}/offers`,
    });

    this.cache.offers = offers;
    return offers;
  };

  public getProducts = (): Promise<IProductListModel> =>
    skymillHttpService.request<IProductListModel>({
      method: HTTP_METHOD.GET,
      url: `${this.urlV2}/products`,
    });

  public createOrder = (
    data: ICreateOrderRequestModel
  ): Promise<IPaymentOrderResponseModel> =>
    skymillHttpService.request<IPaymentOrderResponseModel>({
      data,
      method: HTTP_METHOD.POST,
      url: `${this.urlV2}/orders`,
    });

  public createPaymentDetails = (
    data: ICreatePaymentDetailsRequestModel
  ): Promise<IPaymentOrderResponseModel> =>
    skymillHttpService.request<IPaymentOrderResponseModel>({
      data,
      method: HTTP_METHOD.POST,
      url: `${this.urlV2}/payment-details`,
    });

  public getPaymentInvoices = (params: {
    issued_from: string;
  }): Promise<IPaymentInvoicesListResponseModel> =>
    skymillHttpService.request<IPaymentInvoicesListResponseModel>({
      axiosConfig: {
        params,
      },
      method: HTTP_METHOD.GET,
      url: `${this.url}/invoices`,
    });

  public getInvoicePDF = (invoiceId: string): Promise<Blob> =>
    skymillHttpService.request<Blob>({
      axiosConfig: {
        responseType: "blob",
      },
      method: HTTP_METHOD.GET,
      url: `${this.url}/invoices/${encodeURIComponent(invoiceId)}/pdf`,
    });

  public getPaymentDetails = (): Promise<IPaymentDetailsResponseModel[]> =>
    skymillHttpService.request<IPaymentDetailsResponseModel[]>({
      method: HTTP_METHOD.GET,
      url: `${this.urlV2}/payment-details`,
    });

  public validateCoupon = (
    coupon: string
  ): Promise<IValidateCouponResponseModel> =>
    skymillHttpService.request<IValidateCouponResponseModel>({
      data: {
        code: coupon,
      },
      method: HTTP_METHOD.POST,
      url: `${this.urlV2}/validate-coupon`,
    });

  public getOrderStatus = (
    orderId: string
  ): Promise<IOrderStatusResponseModel> =>
    skymillHttpService.request<IOrderStatusResponseModel>({
      method: HTTP_METHOD.GET,
      url: `${this.urlV2}/order-status/${orderId}`,
    });
}
